/**
 * Phase 11a: Hooks for Schedule of Values (SOV) API.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ─── Query ────────────────────────────────────────────────────────────────────

export function useProjectSOV(projectId: string | undefined) {
  return useQuery({
    queryKey: ['sov', projectId],
    queryFn: () => api.sov.get(projectId!),
    enabled: !!projectId,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 — project simply has no SOV yet
      if (error?.status === 404) return false;
      return failureCount < 2;
    },
  });
}

// ─── Mutation ────────────────────────────────────────────────────────────────

export interface SOVLineItemCreate {
  line_number: number;
  description: string;
  cost_code: string;
  original_budget_amount: number;
  holdback_percent?: number | null;
}

export interface SOVCreateData {
  holdback_percent?: number;
  lender_name?: string | null;
  loan_number?: string | null;
  line_items: SOVLineItemCreate[];
}

export function useCreateSOV(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SOVCreateData) => api.sov.create(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sov', projectId] });
      toast.success('Schedule of Values saved');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save Schedule of Values');
    },
  });
}
